import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import axios from "../../utils/axios";

const fieldCls =
  'w-full px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500/40 focus:border-green-500 transition-all';

export function ForgotPassword(_props: React.ComponentPropsWithoutRef<"form">) {
  const [email, setEmail]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [sent, setSent]         = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');
    axios
      .post('/users/forgot-password', { email })
      .then(() => { setSent(true); })
      .catch((_err: any) => {
        setSent(true); // show success regardless to prevent email enumeration
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="min-h-[calc(100vh-120px)] flex" style={{ background: '#fafaf7' }}>

      {/* ── Left: brand panel ── */}
      <div
        className="hidden lg:flex lg:w-5/12 xl:w-1/2 flex-col justify-between p-12 xl:p-16 relative overflow-hidden"
        style={{ background: '#052e16' }}
      >
        <img
          src="/images/leopard.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 0.18 }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#052e16]/85 via-[#052e16]/65 to-transparent pointer-events-none" />

        {/* Logo */}
        <div className="relative z-10">
          <a href="/" className="flex items-center gap-3">
            <span className="text-2xl font-bold text-white" style={{ fontFamily: 'var(--font-display)' }}>
              WITS
            </span>
            <span className="text-green-400 text-xs tracking-[0.15em] uppercase font-medium mt-1" style={{ fontFamily: 'var(--font-body)' }}>
              by LAPA Africa
            </span>
          </a>
        </div>

        {/* Copy */}
        <div className="relative z-10">
          <h2
            className="text-4xl xl:text-5xl font-bold text-white leading-[1.1] mb-5"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            Secure access,<br />
            <span className="text-green-400">always protected</span>
          </h2>
          <p
            className="text-white/55 text-sm leading-relaxed max-w-sm"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            Your WITS account is protected with end-to-end encryption. If you&apos;ve lost
            access, we&apos;ll help you recover it securely.
          </p>
          <div
            className="mt-10 p-5 rounded-2xl"
            style={{ background: 'rgba(74,222,128,0.07)', border: '1px solid rgba(74,222,128,0.15)' }}
          >
            <p className="text-green-400 text-xs font-semibold tracking-[0.1em] uppercase mb-2" style={{ fontFamily: 'var(--font-body)' }}>
              Need help?
            </p>
            <p className="text-white/55 text-xs leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>
              If you no longer have access to the email on your account, contact us at{' '}
              <a href="mailto:info@lapa.africa" className="text-green-400 hover:text-green-300">
                info@lapa.africa
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* ── Right: form ── */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 lg:px-12 xl:px-16">
        <div className="w-full max-w-md">

          {/* Mobile logo */}
          <a href="/" className="flex items-center gap-2 mb-10 lg:hidden">
            <span className="text-xl font-bold text-gray-900" style={{ fontFamily: 'var(--font-display)' }}>WITS</span>
            <span className="text-green-700 text-xs tracking-[0.12em] uppercase font-medium" style={{ fontFamily: 'var(--font-body)' }}>by LAPA Africa</span>
          </a>

          {sent ? (
            <div className="flex flex-col items-center text-center gap-5 py-10">
              <div className="w-14 h-14 rounded-full bg-green-600 flex items-center justify-center">
                <svg fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2} className="w-7 h-7">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'var(--font-display)' }}>
                Check your inbox
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed max-w-xs" style={{ fontFamily: 'var(--font-body)' }}>
                If <strong>{email}</strong> is registered with WITS, you&apos;ll receive a
                password reset link within a few minutes.
              </p>
              <a
                href="/accounts/login"
                className="mt-2 px-6 py-2.5 rounded-full bg-green-700 text-white text-sm font-semibold hover:bg-green-600 transition-all"
                style={{ fontFamily: 'var(--font-body)' }}
              >
                Back to Sign In
              </a>
            </div>
          ) : (
            <>
              <div className="mb-2">
                <a
                  href="/accounts/login"
                  className="inline-flex items-center gap-1.5 text-gray-400 text-xs hover:text-gray-600 transition-colors mb-8"
                  style={{ fontFamily: 'var(--font-body)' }}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
                  </svg>
                  Back to sign in
                </a>
              </div>

              <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'var(--font-display)' }}>
                Reset password
              </h1>
              <p className="text-gray-500 text-sm mb-8" style={{ fontFamily: 'var(--font-body)' }}>
                Enter the email address associated with your account and we&apos;ll send
                you a reset link.
              </p>

              {errorMsg && (
                <div className="mb-6 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm" style={{ fontFamily: 'var(--font-body)' }}>
                  {errorMsg}
                </div>
              )}

              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div>
                  <label htmlFor="resetEmail" className="block text-xs font-medium text-gray-600 mb-1.5" style={{ fontFamily: 'var(--font-body)' }}>
                    Email Address
                  </label>
                  <input
                    id="resetEmail"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setErrorMsg(''); }}
                    placeholder="you@example.com"
                    required
                    className={fieldCls}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 rounded-full bg-green-700 text-white text-sm font-semibold hover:bg-green-600 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-green-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2"
                  style={{ fontFamily: 'var(--font-body)' }}
                >
                  {loading && <Loader2 className="animate-spin w-4 h-4" />}
                  {loading ? 'Sending…' : 'Send Reset Link'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
